/**
 * React component for unit icon display.
 * Reusable component for displaying unit icon with health bar.
 */

import { getUnitDefinition } from '@shared/constants/units';
import type { UnitType } from '@shared/constants/units';

interface UnitIconProps {
  unitType: UnitType;
  health: number;
  maxHealth: number;
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  clickable?: boolean;
}

export default function UnitIcon({
  unitType,
  health,
  maxHealth,
  size = 'medium',
  onClick,
  clickable = false,
}: UnitIconProps) {
  const definition = getUnitDefinition(unitType);
  // Ensure health is clamped between 0 and maxHealth, and calculate percentage
  const clampedHealth = Math.max(0, Math.min(health, maxHealth));
  const healthPercent = maxHealth > 0 ? (clampedHealth / maxHealth) * 100 : 0;
  // Ensure percentage is between 0 and 100
  const displayPercent = Math.max(0, Math.min(100, healthPercent));

  const sizeClasses = {
    small: 'w-12 h-12',
    medium: 'w-16 h-16',
    large: 'w-20 h-20',
  };

  return (
    <div
      className={`${sizeClasses[size]} flex flex-col items-center gap-1 flex-shrink-0 ${
        clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
      }`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable && onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Unit icon placeholder */}
      <div className="w-full h-full bg-gray-600 rounded border border-gray-500 flex items-center justify-center text-xs text-white flex-shrink-0">
        {definition.name.charAt(0)}
      </div>
      {/* Health bar */}
      <div className="w-full h-1 bg-gray-700 rounded overflow-hidden flex-shrink-0">
        <div
          className={`h-full transition-all ${
            displayPercent > 50
              ? 'bg-green-500'
              : displayPercent > 25
                ? 'bg-yellow-500'
                : 'bg-red-500'
          }`}
          style={{ width: `${displayPercent}%` }}
        />
      </div>
    </div>
  );
}
